import { lazy, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation, useSearchParams } from 'react-router-dom'
import ProtectedRoute from './components/ProtectedRoute'
import { LegacyDatabaseOnboardingRedirect } from './components/LegacyDatabaseOnboardingRedirect'
import OrgInviteParamHandler from './components/OrgInviteParamHandler'
import GitHubInstallationHandler from './components/GitHubInstallationHandler'
import AppLayout from './components/AppLayout'
import { HomeRedirect } from './components/HomeRedirect'
import RequireProjectQuery from './components/RequireProjectQuery'
import { ProjectProvider } from './contexts/ProjectContext'
import { RepositoriesProvider } from './contexts/RepositoriesContext'
import { CustomThemeProvider } from './contexts/ThemeContext'
import { RoleAssumptionProvider } from './contexts/RoleAssumptionContext'
import { PushNotificationProvider } from './contexts/PushNotificationContext'
import { useAuth } from './contexts/AuthContext'
import { getApiUrl } from './config'
import { resetPageMeta } from './utils/pageMeta'
import { fetchBillingRates } from './utils/hostedPricing'
import { DATABASE_DETAIL_TAB_ROUTE_SEGMENTS } from './utils/databaseContextNav'
import { databaseConnectionPath } from './utils/databaseConnectionNav'
import {
  PROJECT_SETTINGS_PAGE_ROUTE_SEGMENTS,
  legacyProjectSettingsHubSegment,
  legacyProjectSettingsSegmentToPage,
  legacyProjectSettingsTabToPage,
  parseProjectSettingsHash,
  parseProjectSettingsTabParam,
  projectNavPagePath,
} from './utils/projectSettingsNav'
import { getSessionToken } from './auth/session'
import {
  ORG_SETTINGS_HUB_PAGE_ROUTE_SEGMENTS,
  legacyOrganizationSettingsHubSegment,
  legacyOrgSettingsSegmentToPage,
  legacyOrgSettingsTabToPage,
  normalizeOrgSettingsTab,
  organizationPagePath,
} from './utils/organizationSettingsNav'
import { TELEMETRY_ROUTE_SEGMENTS, telemetryLegacySearchRedirect } from './utils/telemetryNav'
import {
  ENVIRONMENT_SETTINGS_PAGE_ROUTE_SEGMENTS,
  environmentSettingsLegacySearchRedirect,
} from './utils/environmentSettingsNav'

const AuthBridge = lazy(() => import('./pages/AuthBridge'))
const Billing = lazy(() => import('./pages/Billing'))
const Usage = lazy(() => import('./pages/Usage'))
const SyntheticDatabaseNew = lazy(() => import('./pages/SyntheticDatabaseNew'))
const SyntheticDatabaseSeedPlan = lazy(() => import('./pages/SyntheticDatabaseSeedPlan'))
const DatabaseOnboarding = lazy(() => import('./pages/DatabaseOnboarding'))
const DatabaseConnectExisting = lazy(() => import('./pages/DatabaseConnectExisting'))
const DatabaseDetail = lazy(() => import('./pages/DatabaseDetail'))
const DatabaseSchemaSection = lazy(() => import('./pages/DatabaseSchemaSection'))
const DatabaseClassificationSuggestionsPage = lazy(() => import('./pages/DatabaseClassificationSuggestionsPage'))
const DatabaseSnapshotsPage = lazy(() => import('./pages/DatabaseSnapshotsPage'))
const DatabaseStatisticsPage = lazy(() => import('./pages/DatabaseStatisticsPage'))
const DatabaseSettings = lazy(() => import('./pages/DatabaseSettings'))
const DatabaseRoles = lazy(() => import('./pages/DatabaseRoles'))
const DatabaseRoleDetail = lazy(() => import('./pages/DatabaseRoleDetail'))
const DatabaseRoleRuleDetail = lazy(() => import('./pages/DatabaseRoleRuleDetail'))
const DatabaseUsers = lazy(() => import('./pages/DatabaseUsers'))
const DatabaseDatabaseUsers = lazy(() => import('./pages/DatabaseDatabaseUsers'))
const DatabaseUserDetail = lazy(() => import('./pages/DatabaseUserDetail'))
const ProxyApiKeySetup = lazy(() => import('./pages/ProxyApiKeySetup'))
const Login = lazy(() => import('./pages/Login'))
const CliAuth = lazy(() => import('./pages/CliAuth'))
const CliDevice = lazy(() => import('./pages/CliDevice'))
const Join = lazy(() => import('./pages/Join'))
const Settings = lazy(() => import('./pages/Settings'))
const OrganizationSettings = lazy(() => import('./pages/OrganizationSettings'))
const OriginCallbackPage = lazy(() => import('./origin/OriginCallbackPage'))
const OrganizationTeam = lazy(() => import('./pages/OrganizationTeam'))
const Domains = lazy(() => import('./pages/Domains'))
const Projects = lazy(() => import('./pages/Projects'))
const CreateProject = lazy(() => import('./pages/CreateProject'))
const ProjectOnboarding = lazy(() => import('./pages/ProjectOnboarding'))
const ProjectMobileSetup = lazy(() => import('./pages/ProjectMobileSetup'))
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'))
const ProjectSettings = lazy(() => import('./pages/ProjectSettings'))
const ServiceSettings = lazy(() => import('./pages/ServiceSettings'))
const ProjectEnvironmentDetail = lazy(() => import('./pages/ProjectEnvironmentDetail'))
const ProjectEnvironmentLogs = lazy(() => import('./pages/ProjectEnvironmentLogs'))
const ProjectEnvironmentTelemetry = lazy(() => import('./pages/ProjectEnvironmentTelemetry'))
const ProjectPerformanceAnalysis = lazy(() => import('./pages/ProjectPerformanceAnalysis'))
const ProjectIOS = lazy(() => import('./pages/ProjectIOS'))
const ProjectEnvironmentDeployments = lazy(() => import('./pages/ProjectEnvironmentDeployments'))
const ProjectEnvironmentQAChecks = lazy(() => import('./pages/ProjectEnvironmentQAChecks'))
const ProjectEnvironmentPlaywright = lazy(() => import('./pages/ProjectEnvironmentPlaywright'))
const ProjectEnvironmentDemoPaths = lazy(() => import('./pages/ProjectEnvironmentDemoPaths'))
const ProjectEnvironmentSettings = lazy(() => import('./pages/ProjectEnvironmentSettings'))
const ProjectEnvironmentServiceSettings = lazy(() => import('./pages/ProjectEnvironmentServiceSettings'))
const QualityMaxDetails = lazy(() => import('./pages/QualityMaxDetails'))
const MCPInstructions = lazy(() => import('./pages/MCPInstructions'))
const DeploymentLogs = lazy(() => import('./pages/DeploymentLogs'))
const DeploymentDetail = lazy(() => import('./pages/DeploymentDetail'))
const DeploymentMultiRepoDebug = lazy(() => import('./pages/DeploymentMultiRepoDebug'))
const PleaseWait = lazy(() => import('./pages/PleaseWait'))
const TermsOfService = lazy(() => import('./pages/TermsOfService'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const Feedback = lazy(() => import('./pages/Feedback'))
const RecordingPlayer = lazy(() => import('./pages/RecordingPlayer'))
const RecordingsList = lazy(() => import('./pages/RecordingsList'))

function ProjectPhoneRedirect() {
  const { id, envId } = useParams<{ id: string; envId?: string }>()
  if (!id) return <Navigate to="/projects" replace />
  return <Navigate to={envId ? `/project/${id}/ios/${envId}` : `/project/${id}/ios`} replace />
}

function ProjectIosSettingsRedirect() {
  const { id, envId } = useParams<{ id: string; envId: string }>()
  return <Navigate to={id && envId ? `/project/${id}/ios/${envId}/settings` : '/projects'} replace />
}

function ProjectIosIndexRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={id ? `/project/${id}` : '/projects'} replace />
}

function DatabaseGraphicalRedirect() {
  const { id } = useParams<{ id: string }>()
  if (!id) return <Navigate to="/databases" replace />
  return <Navigate to={`/databases/${id}/schema/diagram`} replace />
}

function DatabaseDiagramRedirect() {
  const { id } = useParams<{ id: string }>()
  if (!id) return <Navigate to="/databases" replace />
  return <Navigate to={`/databases/${id}/schema/diagram`} replace />
}

function DatabaseSchemaRootRedirect() {
  const { id } = useParams<{ id: string }>()
  const { search } = useLocation()
  if (!id) return <Navigate to="/databases" replace />
  return <Navigate to={`/databases/${id}/schema/diagram${search}`} replace />
}

function DatabaseSchemaSectionRoute() {
  return (
    <ProtectedRoute>
      <DatabaseSchemaSection />
    </ProtectedRoute>
  )
}

function DatabaseConnectionRootRedirect() {
  const { id } = useParams<{ id: string }>()
  const { search } = useLocation()
  if (!id) return <Navigate to="/databases" replace />
  return <Navigate to={`${databaseConnectionPath(id, 'upstream')}${search}`} replace />
}

function DatabaseGeneralSettingsRedirect() {
  const { id } = useParams<{ id: string }>()
  if (!id) return <Navigate to="/databases" replace />
  return <Navigate to={`/databases/${id}/general-settings`} replace />
}

function DatabaseGrowthRedirect() {
  const { id } = useParams<{ id: string }>()
  if (!id) return <Navigate to="/databases" replace />
  return <Navigate to={`/databases/${id}/statistics`} replace />
}

function DatabaseSettingsRoute() {
  return (
    <ProtectedRoute>
      <DatabaseSettings />
    </ProtectedRoute>
  )
}

function DatabaseDetailRoute() {
  return (
    <ProtectedRoute>
      <DatabaseDetail />
    </ProtectedRoute>
  )
}

function SlackIntegrationRedirect() {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  params.delete('tab')
  const qs = params.toString()
  return <Navigate to={`/organization/integrations/slack${qs ? `?${qs}` : ''}`} replace />
}

function OrganizationSettingsLegacyRedirect() {
  const { segment } = useParams<{ segment?: string }>()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const page = tabParam
    ? legacyOrgSettingsTabToPage(normalizeOrgSettingsTab(tabParam))
    : legacyOrgSettingsSegmentToPage(segment ?? legacyOrganizationSettingsHubSegment(pathname))
  const next = new URLSearchParams(searchParams)
  next.delete('tab')
  const qs = next.toString()
  return <Navigate to={`${organizationPagePath(page)}${qs ? `?${qs}` : ''}`} replace />
}

function ProjectSettingsLegacyRedirect() {
  const { id, segment } = useParams<{ id: string; segment?: string }>()
  const { pathname, hash } = useLocation()
  const [searchParams] = useSearchParams()
  if (!id) return <Navigate to="/projects" replace />
  const tabParam = searchParams.get('tab')
  const page =
    parseProjectSettingsHash(hash) ??
    (tabParam
      ? legacyProjectSettingsTabToPage(parseProjectSettingsTabParam(tabParam))
      : legacyProjectSettingsSegmentToPage(segment ?? legacyProjectSettingsHubSegment(pathname, id)))
  const next = new URLSearchParams(searchParams)
  next.delete('tab')
  const qs = next.toString()
  return <Navigate to={projectNavPagePath(id, page, qs)} replace />
}

function ProjectSettingsRoute() {
  return (
    <ProtectedRoute>
      <ProjectSettings />
    </ProtectedRoute>
  )
}

function TelemetryLegacyRedirect() {
  const { projectId, envId } = useParams<{ projectId: string; envId: string }>()
  const { search } = useLocation()
  if (!projectId || !envId) return <Navigate to="/projects" replace />
  return <Navigate to={telemetryLegacySearchRedirect(projectId, envId, search)} replace />
}

function OtelLogsPathRedirect() {
  const { projectId, envId } = useParams<{ projectId: string; envId: string }>()
  const { search } = useLocation()
  if (!projectId || !envId) return <Navigate to="/projects" replace />
  return <Navigate to={`/project/${projectId}/environment/${envId}/logs${search}`} replace />
}

function EnvironmentSettingsLegacyRedirect() {
  const { projectId, envId } = useParams<{ projectId: string; envId: string }>()
  const { search } = useLocation()
  if (!projectId || !envId) return <Navigate to="/projects" replace />
  return <Navigate to={environmentSettingsLegacySearchRedirect(projectId, envId, search)} replace />
}

function EnvironmentSettingsRoute() {
  return (
    <ProtectedRoute>
      <ProjectEnvironmentSettings />
    </ProtectedRoute>
  )
}

function OrganizationSettingsRoute() {
  return (
    <ProtectedRoute>
      <OrganizationSettings />
    </ProtectedRoute>
  )
}

const App: React.FC = () => {
  const { isAuthenticated } = useAuth()
  const getToken = () => getSessionToken()

  useEffect(() => {
    const isDevEnvironment = getApiUrl().includes('veilstreamdev.com') || getApiUrl().includes('localhost')
    resetPageMeta(isDevEnvironment)
    void fetchBillingRates()
  }, [])

  return (
    <CustomThemeProvider>
      <RoleAssumptionProvider>
        <ProjectProvider>
          <RepositoriesProvider>
          <Router>
          <OrgInviteParamHandler />
          <GitHubInstallationHandler />
          <PushNotificationProvider isAuthenticated={isAuthenticated} getToken={getToken}>
          <AppLayout>
              <Routes>
                <Route path="/recording/:envSlug" element={<RecordingsList />} />
                <Route path="/recording/:envSlug/:recordingId" element={<RecordingPlayer />} />
                <Route path="/login" element={<Login />} />
                <Route path="/cli/auth" element={
                  <ProtectedRoute>
                    <CliAuth />
                  </ProtectedRoute>
                } />
                <Route path="/cli/login" element={
                  <ProtectedRoute>
                    <CliAuth />
                  </ProtectedRoute>
                } />
                <Route path="/cli/device" element={
                  <ProtectedRoute>
                    <CliDevice />
                  </ProtectedRoute>
                } />
                <Route path="/auth-bridge" element={<AuthBridge />} />
                <Route path="/join" element={<Join />} />
                <Route path="/tos" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/deploying" element={<PleaseWait />} />
                <Route path="/please-wait" element={<PleaseWait />} />
                <Route path="/" element={<HomeRedirect />} />
                <Route path="/home" element={<HomeRedirect />} />

                <Route path="/repositories" element={<Navigate to="/projects" replace />} />
                <Route path="/onboarding" element={<Navigate to="/projects/new" replace />} />
                <Route path="/onboarding/success" element={<Navigate to="/projects" replace />} />
                <Route path="/billing" element={<Navigate to="/organization/billing" replace />} />
                <Route path="/usage" element={<Navigate to="/organization/usage" replace />} />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                } />
                {ORG_SETTINGS_HUB_PAGE_ROUTE_SEGMENTS.filter((segment) => segment !== 'integrations').map((segment) => (
                  <Route
                    key={segment}
                    path={`/organization/${segment}`}
                    element={<OrganizationSettingsRoute />}
                  />
                ))}
                <Route path="/organization/integrations" element={<OrganizationSettingsRoute />} />
                <Route path="/organization/integrations/:sub" element={<OrganizationSettingsRoute />} />
                <Route path="/origin/callback" element={
                  <ProtectedRoute>
                    <OriginCallbackPage />
                  </ProtectedRoute>
                } />
                <Route path="/organization/settings/:segment" element={<OrganizationSettingsLegacyRedirect />} />
                <Route path="/organization/settings" element={<OrganizationSettingsLegacyRedirect />} />
                <Route path="/organization/team" element={
                  <ProtectedRoute>
                    <OrganizationTeam />
                  </ProtectedRoute>
                } />
                <Route path="/organization/usage" element={
                  <ProtectedRoute>
                    <Usage />
                  </ProtectedRoute>
                } />
                <Route path="/organization/billing" element={
                  <ProtectedRoute>
                    <Billing />
                  </ProtectedRoute>
                } />
                <Route path="/settings/integrations/slack" element={
                  <ProtectedRoute>
                    <SlackIntegrationRedirect />
                  </ProtectedRoute>
                } />
                <Route path="/domains" element={
                  <ProtectedRoute>
                    <Navigate to="/projects" replace />
                  </ProtectedRoute>
                } />
                <Route path="/projects" element={
                  <ProtectedRoute>
                    <Projects />
                  </ProtectedRoute>
                } />
                <Route path="/projects/new" element={
                  <ProtectedRoute>
                    <CreateProject />
                  </ProtectedRoute>
                } />
                <Route path="/project/:id" element={
                  <ProtectedRoute>
                    <ProjectDetail />
                  </ProtectedRoute>
                } />
                <Route path="/project/:id/deploy" element={
                  <ProtectedRoute>
                    <ProjectOnboarding />
                  </ProtectedRoute>
                } />
                <Route path="/project/:id/setup/mobile" element={
                  <ProtectedRoute>
                    <ProjectMobileSetup />
                  </ProtectedRoute>
                } />
                <Route path="/project/:id/domains" element={
                  <ProtectedRoute>
                    <Domains />
                  </ProtectedRoute>
                } />
                <Route path="/project/:id/performance-analysis/:envId" element={
                  <ProtectedRoute>
                    <ProjectPerformanceAnalysis />
                  </ProtectedRoute>
                } />
                <Route path="/project/:id/performance-analysis" element={
                  <ProtectedRoute>
                    <ProjectPerformanceAnalysis />
                  </ProtectedRoute>
                } />
                <Route path="/project/:id/ios/new" element={<ProjectIosIndexRedirect />} />
                <Route path="/project/:id/ios/:envId/settings" element={
                  <ProtectedRoute>
                    <ProjectIOS />
                  </ProtectedRoute>
                } />
                <Route path="/project/:id/ios/:envId/edit" element={<ProjectIosSettingsRedirect />} />
                <Route path="/project/:id/ios/:envId" element={
                  <ProtectedRoute>
                    <ProjectIOS />
                  </ProtectedRoute>
                } />
                <Route path="/project/:id/ios" element={<ProjectIosIndexRedirect />} />
                <Route path="/project/:id/phone/:envId" element={<ProjectPhoneRedirect />} />
                <Route path="/project/:id/phone" element={<ProjectPhoneRedirect />} />
                {PROJECT_SETTINGS_PAGE_ROUTE_SEGMENTS.map((segment) => (
                  <Route
                    key={segment}
                    path={`/project/:id/${segment}`}
                    element={<ProjectSettingsRoute />}
                  />
                ))}
                <Route path="/project/:id/settings/:segment" element={<ProjectSettingsLegacyRedirect />} />
                <Route path="/project/:id/settings" element={<ProjectSettingsLegacyRedirect />} />
                <Route path="/project/:id/service/:mappingId/settings" element={
                  <ProtectedRoute>
                    <ServiceSettings />
                  </ProtectedRoute>
                } />
                <Route path="/project/:projectId/environment/:envId" element={
                  <ProtectedRoute>
                    <ProjectEnvironmentDetail />
                  </ProtectedRoute>
                } />
                <Route path="/project/:projectId/environment/:envId/logs" element={
                  <ProtectedRoute>
                    <ProjectEnvironmentLogs />
                  </ProtectedRoute>
                } />
                <Route path="/project/:projectId/environment/:envId/otel-logs" element={
                  <ProtectedRoute>
                    <OtelLogsPathRedirect />
                  </ProtectedRoute>
                } />
                {TELEMETRY_ROUTE_SEGMENTS.map((segment) => (
                  <Route
                    key={segment}
                    path={`/project/:projectId/environment/:envId/${segment}`}
                    element={
                      <ProtectedRoute>
                        <ProjectEnvironmentTelemetry />
                      </ProtectedRoute>
                    }
                  />
                ))}
                <Route path="/project/:projectId/environment/:envId/telemetry" element={
                  <ProtectedRoute>
                    <TelemetryLegacyRedirect />
                  </ProtectedRoute>
                } />
                <Route path="/project/:projectId/environment/:envId/deployments" element={
                  <ProtectedRoute>
                    <ProjectEnvironmentDeployments />
                  </ProtectedRoute>
                } />
                <Route path="/project/:projectId/environment/:envId/qa-checks" element={
                  <ProtectedRoute>
                    <ProjectEnvironmentQAChecks />
                  </ProtectedRoute>
                } />
                <Route path="/project/:projectId/environment/:envId/playwright" element={
                  <ProtectedRoute>
                    <ProjectEnvironmentPlaywright />
                  </ProtectedRoute>
                } />
                <Route path="/project/:projectId/environment/:envId/demo-paths" element={
                  <ProtectedRoute>
                    <ProjectEnvironmentDemoPaths />
                  </ProtectedRoute>
                } />
                {ENVIRONMENT_SETTINGS_PAGE_ROUTE_SEGMENTS.map((segment) => (
                  <Route
                    key={segment}
                    path={`/project/:projectId/environment/:envId/${segment}`}
                    element={<EnvironmentSettingsRoute />}
                  />
                ))}
                <Route path="/project/:projectId/environment/:envId/settings" element={
                  <ProtectedRoute>
                    <EnvironmentSettingsLegacyRedirect />
                  </ProtectedRoute>
                } />
                <Route path="/project/:projectId/environment/:envId/service/:mappingId/settings" element={
                  <ProtectedRoute>
                    <ProjectEnvironmentServiceSettings />
                  </ProtectedRoute>
                } />
                <Route path="/project/:projectId/environment/:envId/qa_check/:qaCheckId" element={
                  <ProtectedRoute>
                    <QualityMaxDetails />
                  </ProtectedRoute>
                } />
                <Route path="/settings/mcp-instructions" element={
                  <ProtectedRoute>
                    <MCPInstructions />
                  </ProtectedRoute>
                } />
                <Route path="/feedback" element={
                  <ProtectedRoute>
                    <Feedback />
                  </ProtectedRoute>
                } />
                <Route path="/snapshots" element={<Navigate to="/projects" replace />} />
                <Route path="/databases" element={<Navigate to="/projects" replace />} />
                <Route path="/synthetic-databases" element={<Navigate to="/projects" replace />} />
                <Route path="/synthetic-databases/new" element={
                  <ProtectedRoute>
                    <RequireProjectQuery>
                      <SyntheticDatabaseNew />
                    </RequireProjectQuery>
                  </ProtectedRoute>
                } />
                <Route path="/synthetic-databases/:id/seed-plan" element={
                  <ProtectedRoute>
                    <SyntheticDatabaseSeedPlan />
                  </ProtectedRoute>
                } />
                <Route path="/synthetic-databases/:id" element={
                  <ProtectedRoute>
                    <SyntheticDatabaseSeedPlan />
                  </ProtectedRoute>
                } />
                <Route path="/database/onboarding" element={
                  <ProtectedRoute>
                    <LegacyDatabaseOnboardingRedirect />
                  </ProtectedRoute>
                } />
                <Route path="/database/new" element={
                  <ProtectedRoute>
                    <RequireProjectQuery>
                      <DatabaseOnboarding />
                    </RequireProjectQuery>
                  </ProtectedRoute>
                } />
                <Route path="/database/connect" element={
                  <ProtectedRoute>
                    <RequireProjectQuery>
                      <DatabaseConnectExisting />
                    </RequireProjectQuery>
                  </ProtectedRoute>
                } />
                      <Route path="/databases/roles" element={
                        <ProtectedRoute>
                          <DatabaseRoles />
                        </ProtectedRoute>
                      } />
                      <Route path="/databases/rules" element={
                        <ProtectedRoute>
                          <Navigate to="/databases" replace />
                        </ProtectedRoute>
                      } />
                      <Route path="/databases/rules/new" element={
                        <ProtectedRoute>
                          <Navigate to="/databases" replace />
                        </ProtectedRoute>
                      } />
                      <Route path="/databases/roles/:id" element={
                        <ProtectedRoute>
                          <DatabaseRoleDetail />
                        </ProtectedRoute>
                      } />
                      <Route path="/databases/roles/:roleId/rules/new" element={
                        <ProtectedRoute>
                          <DatabaseRoleRuleDetail />
                        </ProtectedRoute>
                      } />
                      <Route path="/databases/roles/:roleId/rules/:ruleId" element={
                        <ProtectedRoute>
                          <DatabaseRoleRuleDetail />
                        </ProtectedRoute>
                      } />
                      <Route path="/databases/:databaseId/rules/new" element={
                        <ProtectedRoute>
                          <DatabaseRoleRuleDetail />
                        </ProtectedRoute>
                      } />
                      <Route path="/databases/:databaseId/rules/:ruleId" element={
                        <ProtectedRoute>
                          <DatabaseRoleRuleDetail />
                        </ProtectedRoute>
                      } />
                <Route path="/databases/users" element={
                  <ProtectedRoute>
                    <DatabaseUsers />
                  </ProtectedRoute>
                } />
                <Route path="/databases/user/:id" element={
                  <ProtectedRoute>
                    <DatabaseUserDetail />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/proxy-api-key/setup" element={
                  <ProtectedRoute>
                    <ProxyApiKeySetup />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/settings" element={
                  <ProtectedRoute>
                    <DatabaseGeneralSettingsRedirect />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/general-settings" element={<DatabaseSettingsRoute />} />
                <Route path="/databases/:id/snapshots" element={
                  <ProtectedRoute>
                    <DatabaseSnapshotsPage />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/statistics" element={
                  <ProtectedRoute>
                    <DatabaseStatisticsPage />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/growth" element={
                  <ProtectedRoute>
                    <DatabaseGrowthRedirect />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/users" element={
                  <ProtectedRoute>
                    <DatabaseDatabaseUsers />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/classification-suggestions" element={
                  <ProtectedRoute>
                    <DatabaseClassificationSuggestionsPage />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/schema/diagram" element={<DatabaseSchemaSectionRoute />} />
                <Route path="/databases/:id/schema/table" element={<DatabaseSchemaSectionRoute />} />
                <Route path="/databases/:id/schema" element={
                  <ProtectedRoute>
                    <DatabaseSchemaRootRedirect />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/connection/upstream" element={<DatabaseDetailRoute />} />
                <Route path="/databases/:id/connection/downstream" element={<DatabaseDetailRoute />} />
                <Route path="/databases/:id/connection/parameters" element={<DatabaseDetailRoute />} />
                <Route path="/databases/:id/connection" element={
                  <ProtectedRoute>
                    <DatabaseConnectionRootRedirect />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/diagram" element={
                  <ProtectedRoute>
                    <DatabaseDiagramRedirect />
                  </ProtectedRoute>
                } />
                <Route path="/databases/:id/graphical" element={
                  <ProtectedRoute>
                    <DatabaseGraphicalRedirect />
                  </ProtectedRoute>
                } />
                {DATABASE_DETAIL_TAB_ROUTE_SEGMENTS.map((segment) => (
                  <Route
                    key={segment}
                    path={`/databases/:id/${segment}`}
                    element={<DatabaseDetailRoute />}
                  />
                ))}
                <Route path="/databases/:id" element={<DatabaseDetailRoute />} />

                <Route path="/deployment/:deploymentId/multi-repo-debug" element={
                  <ProtectedRoute>
                    <DeploymentMultiRepoDebug />
                  </ProtectedRoute>
                } />
                <Route path="/deployment/:deploymentId/logs" element={
                  <ProtectedRoute>
                    <DeploymentLogs />
                  </ProtectedRoute>
                } />
                <Route path="/deployment/:deploymentId" element={
                  <ProtectedRoute>
                    <DeploymentDetail />
                  </ProtectedRoute>
                } />
              </Routes>
          </AppLayout>
          </PushNotificationProvider>
          </Router>
          </RepositoriesProvider>
        </ProjectProvider>
      </RoleAssumptionProvider>
    </CustomThemeProvider>
  )
}

export default App
